import { Component, computed, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { DialogService } from '@design-system/lib/dialog/dialog.service';
import { CreateItemInFlightService } from '@app/core/services/create-item-in-flight.service';
import { MasterTablesService } from '@app/core/services/master-tables.service';
import { HierarchyNode } from '@app/core/models/data-dictionary.model';
import { forkJoin } from 'rxjs';
import {
  buildMasterTableAllowedCodeSet,
  fieldCodeToDisplay,
  isRawInMasterCodeSet,
} from '@app/core/utils/field-code-format.util';
import { getDictionaryDefinitionImportPkErrors } from '@app/core/utils/dictionary-pk-import.util';

interface DialogRef { dismiss: () => void }

export interface InitialFieldData {
  position: number;
  subPosition: number;
  fieldName: string;
  dataTypeId: number;
  description: string;
}

export interface CreateItemData {
  type: 'folder' | 'table';
  name: string;
  parentId: number | null;
  initialField?: InitialFieldData;
  csvFields?: Record<string, string>[];
}

@Component({
  selector: 'app-create-item-modal',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './create-item-modal.component.html',
  styleUrl: './create-item-modal.component.scss',
})
export class CreateItemModalComponent implements OnInit, OnDestroy {
  private readonly dialogService = inject(DialogService);
  private readonly masterTablesService = inject(MasterTablesService);
  /** Exposed for template: disabled state, aria-busy, and label. */
  readonly createItemInFlight = inject(CreateItemInFlightService);
  private hostDialogRef?: DialogRef;

  @Input() parentNode: HierarchyNode | null = null;
  @Input() currentDepth = 0;
  @Input() renderAsDialogContent = false;
  @Input() dialogRef?: DialogRef;
  @Input() onCloseRequest?: () => void;
  @Input() onCreateRequest?: (data: CreateItemData) => void;
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() close = new EventEmitter<void>();
  @Output() create = new EventEmitter<CreateItemData>();

  itemType = signal<'folder' | 'table'>('folder');
  itemName = signal<string>('');
  nameError = signal<string>('');

  creationMethod = signal<'scratch' | 'csv'>('scratch');

  fieldPosition = signal<number>(1);
  fieldSubPosition = signal<number>(1);
  fieldName = signal<string>('');
  fieldDescription = signal<string>('');
  fieldDataTypeId = signal<number>(0);
  fieldNameError = signal<string>('');

  selectedFile = signal<File | null>(null);
  isDragging = signal<boolean>(false);
  hasError = signal<boolean>(false);
  validationErrors = signal<string[]>([]);
  validationWarnings = signal<string[]>([]);
  previewData = signal<Record<string, string>[]>([]);
  previewColumns = signal<string[]>([]);

  /** Data type options for the scratch form (master rows). */
  dataTypeOptions = signal<{ id: number; code: string }[]>([]);
  private masterCatalogsLoaded = false;
  private allowedDataTypeCodes = new Set<string>();
  private lastParsedCsvText: string | null = null;

  isSubmitDisabled = computed(() => {
    if (this.createItemInFlight.isInFlight()) {
      return true;
    }
    if (!this.itemName()) {
      return true;
    }
    if (this.itemType() === 'table') {
      if (this.creationMethod() === 'scratch') {
        return !this.fieldName();
      }
      return !this.selectedFile() || this.hasError() || this.validationErrors().length > 0;
    }
    return false;
  });

  ngOnInit(): void {
    this.loadMasterCatalogs();
    if (!this.renderAsDialogContent) {
      this.openDialog();
      return;
    }

    if (this.canCreateFolder && !this.canCreateTable) {
      this.itemType.set('folder');
    } else if (this.canCreateTable && !this.canCreateFolder) {
      this.itemType.set('table');
    } else if (this.canCreateFolder) {
      this.itemType.set('folder');
    }
  }

  private loadMasterCatalogs(): void {
    forkJoin({
      dataTypes: this.masterTablesService.getDataTypes(),
    }).subscribe({
      next: ({ dataTypes }) => {
        this.allowedDataTypeCodes = buildMasterTableAllowedCodeSet(dataTypes);
        this.masterCatalogsLoaded = true;
        const options = dataTypes.length
          ? dataTypes.map((d) => ({ id: d.id, code: fieldCodeToDisplay(d.code) }))
          : this.dataTypeOptions();
        this.dataTypeOptions.set(options);
        if ((!this.fieldDataTypeId() || this.fieldDataTypeId() === 0) && options.length > 0) {
          this.fieldDataTypeId.set(options[0].id);
        }
        if (this.lastParsedCsvText) {
          this.parseCSV(this.lastParsedCsvText);
        }
      },
      error: (err) => {
        console.error('Failed to load master catalogs (create item)', err);
        this.masterCatalogsLoaded = true;
        this.allowedDataTypeCodes = new Set();
        if (this.lastParsedCsvText) {
          this.parseCSV(this.lastParsedCsvText);
        }
      },
    });
  }

  ngOnDestroy(): void {
    if (!this.renderAsDialogContent) {
      this.hostDialogRef?.dismiss();
      this.hostDialogRef = undefined;
    }
  }

  private openDialog(): void {
    this.hostDialogRef = this.dialogService.show({
      title: 'Create New Item',
      component: CreateItemModalComponent,
      componentInputs: {
        renderAsDialogContent: true,
        parentNode: this.parentNode,
        currentDepth: this.currentDepth,
        onCloseRequest: () => this.close.emit(),
        onCreateRequest: (data: CreateItemData) => this.create.emit(data),
      },
      withDialogRef: true,
      canCloseWithBackdrop: false,
      width: '640px',
      maxWidth: '95vw',
      headerStyles: { display: 'none' },
      footerStyles: { display: 'none' },
      bodyStyles: { padding: '0', overflow: 'hidden' },
    });
  }

  get canCreateFolder(): boolean {
    return this.currentDepth < 2;
  }

  get canCreateTable(): boolean {
    return this.currentDepth >= 2;
  }

  get parentName(): string {
    return this.parentNode?.name || 'Root';
  }

  get depthLabel(): string {
    if (this.currentDepth === 0) return 'Level 1 (Root)';
    if (this.currentDepth === 1) return 'Level 2';
    return 'Level 3 (Maximum)';
  }

  selectType(type: 'folder' | 'table'): void {
    if (type === 'folder' && !this.canCreateFolder) return;
    if (type === 'table' && !this.canCreateTable) return;
    this.itemType.set(type);
    if (type === 'folder') {
      this.creationMethod.set('scratch');
      this.resetCsvState();
    }
  }

  selectCreationMethod(method: 'scratch' | 'csv'): void {
    this.creationMethod.set(method);
    if (method === 'scratch') {
      this.resetCsvState();
    }
  }

  private resetCsvState(): void {
    this.selectedFile.set(null);
    this.isDragging.set(false);
    this.hasError.set(false);
    this.validationErrors.set([]);
    this.validationWarnings.set([]);
    this.previewData.set([]);
    this.previewColumns.set([]);
    this.lastParsedCsvText = null;
  }

  onItemNameChange(value: string): void {
    this.itemName.set(value);
    this.createItemInFlight.clearError();
  }

  validateName(): boolean {
    const name = this.itemName().trim();
    if (!name) {
      this.nameError.set('Name is required');
      return false;
    }
    if (name.length < 2) {
      this.nameError.set('Name must be at least 2 characters');
      return false;
    }
    this.nameError.set('');
    return true;
  }

  validateFieldName(): boolean {
    if (this.itemType() !== 'table') return true;
    if (this.creationMethod() === 'csv') return true;

    const name = this.fieldName().trim();
    if (!name) {
      this.fieldNameError.set('Field name is required');
      return false;
    }
    if (name.length < 2) {
      this.fieldNameError.set('Field name must be at least 2 characters');
      return false;
    }
    this.fieldNameError.set('');
    return true;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragging.set(false);
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.processFile(input.files[0]);
    }
  }

  removeFile(): void {
    this.resetCsvState();
  }

  private processFile(file: File): void {
    this.selectedFile.set(file);
    this.validationErrors.set([]);
    this.hasError.set(false);

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      this.lastParsedCsvText = content;
      this.parseCSV(content);
    };
    reader.onerror = () => {
      this.validationErrors.set(['Failed to read file']);
      this.hasError.set(true);
    };
    reader.readAsText(file);
  }

  private parseCSV(content: string): void {
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      this.validationErrors.set(['File must contain headers and at least one data row']);
      this.hasError.set(true);
      return;
    }

    const headers = this.parseCSVLine(lines[0]);
    this.previewColumns.set(headers);

    const requiredHeaders = ['Field Name', 'Data Type', 'Description'];
    const missing = requiredHeaders.filter(h =>
      !headers.some(header => header.toLowerCase() === h.toLowerCase())
    );

    if (missing.length > 0) {
      this.validationErrors.set([`Missing required columns: ${missing.join(', ')}`]);
      this.hasError.set(true);
      return;
    }

    const knownHeaders = new Set([
      'field name', 'fieldname',
      'data type', 'datatype',
      'description',
      'position',
      'sub position', 'sub_position', 'subposition',
    ]);
    const unknown = headers.filter(h => !knownHeaders.has(h.trim().toLowerCase()));
    if (unknown.length > 0) {
      this.validationWarnings.set([
        `The following columns are not recognized and will be ignored: ${unknown.join(', ')}`,
      ]);
    } else {
      this.validationWarnings.set([]);
    }

    if (!this.masterCatalogsLoaded) {
      this.validationErrors.set(['Loading allowed data types…']);
      this.hasError.set(true);
      const dataPending: Record<string, string>[] = [];
      for (let i = 1; i < lines.length; i++) {
        const values = this.parseCSVLine(lines[i]);
        const row: Record<string, string> = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        dataPending.push(row);
      }
      this.previewData.set(dataPending);
      return;
    }
    if (this.allowedDataTypeCodes.size === 0) {
      this.validationErrors.set([
        'No data type codes are available. Check your connection and try again.',
      ]);
      this.hasError.set(true);
      const dataEmpty: Record<string, string>[] = [];
      for (let i = 1; i < lines.length; i++) {
        const values = this.parseCSVLine(lines[i]);
        const row: Record<string, string> = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        dataEmpty.push(row);
      }
      this.previewData.set(dataEmpty);
      return;
    }

    const data: Record<string, string>[] = [];
    const errors: string[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCSVLine(lines[i]);
      const row: Record<string, string> = {};

      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });

      const dataType = row['Data Type'] || row['data type'] || row['DataType'];
      if (
        this.allowedDataTypeCodes.size > 0 &&
        dataType &&
        !isRawInMasterCodeSet(dataType, {
          allowEmpty: true,
          allowed: this.allowedDataTypeCodes,
        })
      ) {
        errors.push(`Row ${i + 1}: Invalid data type "${dataType}"`);
      }

      data.push(row);
    }

    for (const msg of getDictionaryDefinitionImportPkErrors(data, {
      importIsAppend: false,
      existingFields: [],
    })) {
      errors.push(msg);
    }

    this.previewData.set(data);

    if (errors.length > 0) {
      this.validationErrors.set(errors.slice(0, 5));
      if (errors.length > 5) {
        this.validationErrors.update(errs => [...errs, `... and ${errors.length - 5} more errors`]);
      }
      this.hasError.set(true);
    }
  }

  private parseCSVLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current.trim());
    return result;
  }

  onSubmit(): void {
    if (!this.createItemInFlight.tryStart()) {
      return;
    }
    if (!this.validateName()) {
      this.createItemInFlight.clear();
      return;
    }
    if (!this.validateFieldName()) {
      this.createItemInFlight.clear();
      return;
    }

    const data: CreateItemData = {
      type: this.itemType(),
      name: this.itemName().trim(),
      parentId: this.parentNode != null ? this.parentNode.id : null,
    };

    if (this.itemType() === 'table') {
      if (this.creationMethod() === 'csv') {
        data.csvFields = this.previewData();
      } else {
        data.initialField = {
          position: 1,
          subPosition: 1,
          fieldName: this.fieldName().trim(),
          dataTypeId: this.fieldDataTypeId(),
          description: this.fieldDescription().trim(),
        };
      }
    }

    if (this.renderAsDialogContent) {
      this.onCreateRequest?.(data);
      return;
    }

    this.create.emit(data);
  }

  onClose(): void {
    if (this.renderAsDialogContent) {
      this.onCloseRequest?.();
      return;
    }

    this.close.emit();
  }
}
